import React from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Custom styles for the carousel
const carouselStyles = `
  .swiper-pagination {
    position: relative !important;
    margin-top: 16px !important;
  }
  .swiper-pagination-bullet {
    background-color: #1976d2 !important;
    opacity: 0.3 !important;
  }
  .swiper-pagination-bullet-active {
    opacity: 1 !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = carouselStyles;
  document.head.appendChild(styleSheet);
}

const ExerciseDetails = ({ exerciseData }) => {
  if (!exerciseData) return null;



  const { reportData } = exerciseData;
  const exercises = reportData?.exercises || [];

  if (exercises.length === 0) return null;

  const renderNormalSet = (set, idx) => (
    <Box
      key={idx}
      sx={{
        py: 1.5,
        px: 2,
        mb: 2,
        backgroundColor: set.completed ? "#e8f5e8" : "#f5f5f5",
        borderRadius: 1,
        border: set.completed ? "1px solid #4caf50" : "1px solid #e0e0e0",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Set {idx + 1}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2">
          {set.completedReps || set.reps} / {set.reps} reps
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {set.weight} lbs
        </Typography>
      </Box>

      {set.completed && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
          <Chip
            label="✓ Completed"
            size="small"
            color="success"
            sx={{ fontSize: "0.75rem", height: 20 }}
          />
        </Box>
      )}
    </Box>
  );

  const renderSuperSetExercise = (exercise, exerciseIdx) => (
    <Box key={exercise.id || exerciseIdx} sx={{ mb: 2 }}>
      <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
        {exercise.exerciseName}
      </Typography>

      <Box
        sx={{
          py: 1,
          px: 2,
          backgroundColor: "#f5f5f5",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Rest: {exercise.rest}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            {exercise.completedReps || exercise.reps} / {exercise.reps} reps
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {exercise.weight} lbs
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderSuperSet = (exercise) => (
    <Card sx={{ mb: 3, boxShadow: 1 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Chip
              label="SUPER SET"
              color="error"
              variant="outlined"
              size="small"
              sx={{ mr: 1.5 }}
            />
            <Typography variant="h6" fontWeight="bold">
              {exercise.superSetName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            {exercise.completed && (
              <Chip
                label="Completed"
                color="success"
                size="small"
                sx={{ height: 24 }}
              />
            )}
          </Box>
        </Box>

        {exercise.sets &&
          exercise.sets.map((set, setIdx) => (
            <Box key={set.id || setIdx} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                Set {setIdx + 1}
              </Typography>
              {set.exercises &&
                set.exercises.map((subExercise, exerciseIdx) =>
                  renderSuperSetExercise(subExercise, exerciseIdx),
                )}
              {setIdx < exercise.sets.length - 1 && (
                <Box sx={{ borderBottom: "1px solid #e0e0e0", mt: 2, mb: 2 }} />
              )}
            </Box>
          ))}
      </CardContent>
    </Card>
  );

  const renderNormalExercise = (exercise) => (
    <Card sx={{ mb: 3, boxShadow: 1 }}>
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mr: 2 }}>
              {exercise.exerciseName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              {exercise.sets?.length || 0} Sets
            </Typography>
            {exercise.completed && (
              <Chip label="Completed" color="success" size="small" />
            )}
          </Box>
        </Box>

        {exercise.notes && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            Notes: {exercise.notes}
          </Typography>
        )}

        {exercise.sets &&
          exercise.sets.map((set, idx) => renderNormalSet(set, idx))}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Exercise Details
      </Typography>

      {exercises.length > 0 && (
        <Box
          sx={{ position: "relative", "& .swiper": { overflow: "visible" } }}
        >
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              prevEl: ".swiper-button-prev-exercises",
              nextEl: ".swiper-button-next-exercises",
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            style={{ height: "40vh" }} // Set a fixed height for the carousel
          >
            {exercises.map((exercise, index) => (
              <SwiperSlide key={exercise.id || index}>
                <Box
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    pr: 1, // Add padding for scrollbar
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(0,0,0,0.1)",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.3)",
                      borderRadius: "3px",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.5)",
                      },
                    },
                  }}
                >
                  {exercise.type === "super_set"
                    ? renderSuperSet(exercise)
                    : renderNormalExercise(exercise)}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          {exercises.length > 1 && (
            <>
              <IconButton
                className="swiper-button-prev-exercises"
                sx={{
                  position: "absolute",
                  left: -20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                  boxShadow: 1,
                }}
                size="small"
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              <IconButton
                className="swiper-button-next-exercises"
                sx={{
                  position: "absolute",
                  right: -20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                  boxShadow: 1,
                }}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ExerciseDetails;
